import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAiRecommendSchedule, useConfirmSchedule } from 'hooks/useSchedule';
import { scheduleStore } from 'store/scheduleStore';
import {
    AppScreen, Badge, Button, ButtonFooter, PageIntro, Section, SectionHeader,
} from 'components/ds';
import styles from './ScheduleResult.module.css';

export const ScheduleResult = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR';
    const { status: recommendStatus, data: aiRecommendData } = useAiRecommendSchedule();
    const { status: confirmStatus, data: confirmData } = useConfirmSchedule();

    // 컴포넌트 마운트 시 AI 추천 일정 조회
    useEffect(() => {
        if (meetingId) {
            scheduleStore.onGetAiRecommendSchedule(meetingId);
        }
    }, [meetingId]);

    // API 데이터를 optimalSlots 형식으로 변환
    const optimalSlots = aiRecommendData?.recommendations?.map((rec: any) => ({
        id: rec.scheduleId,
        date: new Date(rec.startDateTime).toLocaleDateString(dateLocale, {
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        }),
        time: `${new Date(rec.startDateTime).toLocaleTimeString(dateLocale, {
            hour: '2-digit',
            minute: '2-digit'
        })} - ${new Date(rec.endDateTime).toLocaleTimeString(dateLocale, {
            hour: '2-digit',
            minute: '2-digit'
        })}`,
        availableCount: rec.availableCount,
        totalCount: rec.totalCount,
        participants: rec.availableMembers?.map((m: any) => m.nickname) || [],
        scheduleId: rec.scheduleId
    })) || [];

    const [selectedSlot, setSelectedSlot] = useState(optimalSlots[0] || null);

    // optimalSlots가 업데이트되면 첫 번째 슬롯 선택
    useEffect(() => {
        if (optimalSlots.length > 0 && !selectedSlot) {
            setSelectedSlot(optimalSlots[0]);
        }
    }, [optimalSlots, selectedSlot]);

    const handleConfirmTime = () => {
        if (selectedSlot) {
            scheduleStore.onConfirmSchedule(selectedSlot.scheduleId);
        }
    };

    // 일정 확정 성공 시 모임 상세로 이동
    useEffect(() => {
        if (confirmStatus === 'ready' && confirmData?.success) {
            navigate(`/meeting/detail/${meetingId}`, { replace: true });
        }
    }, [confirmStatus, confirmData, navigate, meetingId]);

    // 응답 전에는 분석 중, 응답이 비었거나 실패하면 그렇다고 알린다 (예전엔 끝없이 "분석 중"에 머물렀다)
    if (!selectedSlot || optimalSlots.length === 0) {
        const settled = recommendStatus === 'ready' || recommendStatus === 'error';
        return (
            <AppScreen title={t('scheduleResult.title')} bar="sub" hasFooter={settled}>
                <p className={styles.state}>
                    {!settled
                        ? t('scheduleResult.analyzing')
                        : recommendStatus === 'error' ? t('scheduleResult.loadFailed') : t('scheduleResult.empty')}
                </p>
                {settled && (
                    <ButtonFooter>
                        <Button variant="primary" size="lg" block onClick={() => navigate(`/schedule/main/${meetingId}`, { replace: true })}>
                            {t('scheduleResult.backToSchedule')}
                        </Button>
                    </ButtonFooter>
                )}
            </AppScreen>
        );
    }

    return (
        <AppScreen title={t('scheduleResult.title')} bar="sub" hasFooter>
            <PageIntro title={t('scheduleResult.foundTitle')} desc={t('scheduleResult.foundDesc')} />

            {/* 가장 많이 되는 시간 */}
            <Section spacing="tight">
                <SectionHeader title={t('scheduleResult.topRecommend')} />
                <div className={styles.top}>
                    <div className={styles.topHead}>
                        <span className={styles.topWhen}>{selectedSlot.date}</span>
                        <Badge tone="brand">
                            {selectedSlot.availableCount === selectedSlot.totalCount
                                ? t('scheduleResult.allAvailable')
                                : t('scheduleResult.countAvailable', { count: selectedSlot.availableCount })}
                        </Badge>
                    </div>
                    <div className={styles.topTime}>{selectedSlot.time}</div>
                    <div className={styles.topPeopleLabel}>{t('scheduleResult.availableMembers')}</div>
                    <div className={styles.topPeople}>
                        {selectedSlot.participants.map((name: string, index: number) => (
                            <span key={index} className={styles.chip}>{name}</span>
                        ))}
                    </div>
                </div>
            </Section>

            {/* 다른 후보 */}
            {optimalSlots.length > 1 && (
                <Section divided>
                    <SectionHeader title={t('scheduleResult.otherTimes')} count={optimalSlots.length - 1} />
                    <ul className={styles.options}>
                        {optimalSlots.slice(1).map((slot: any) => (
                            <li key={slot.id}>
                                <button
                                    type="button"
                                    className={selectedSlot.id === slot.id
                                        ? `${styles.option} ${styles.optionOn}`
                                        : styles.option}
                                    onClick={() => setSelectedSlot(slot)}
                                >
                                    <span className={styles.optionTop}>
                                        <span className={styles.optionWhen}>
                                            {slot.date} · {slot.time}
                                        </span>
                                        <span className={styles.optionCount}>
                                            <strong>{slot.availableCount}</strong>
                                            {t('scheduleResult.countSuffix', { total: slot.totalCount })}
                                        </span>
                                    </span>
                                    <span className={styles.optionPeople}>
                                        {slot.participants.join(', ')}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            <Section divided last>
                <p className={styles.notice}>{t('scheduleResult.notice')}</p>
            </Section>

            <ButtonFooter>
                <Button variant="primary" size="lg" block onClick={handleConfirmTime}>
                    {t('scheduleResult.confirmBtn')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
